import { useMemo } from 'react';
import {
  Banner,
  Box,
  Stack,
  Typography,
  Card,
  CardContent,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { Grid } from '@fortress-technology-solutions/fortress-component-library/Molecules';
import { formatCurrency } from '@fortress-technology-solutions/fortress-component-library/utils';
import SubmitToTracsModal from '../SubmitToTracsModal';
import PrepareExecutionModal from './PrepareExecutionModal';
import AccountingSectionTable from './AccountingSectionTable';
import useIntl from '../../../hooks/useIntl';
import messages from './messages';
import parentMessages from '../messages';
import { getFirstMatchingReason } from '../utils';

const formatNullableCurrency = (value) => {
  return formatCurrency(value ?? 0);
};

const ListItem = ({ label, value }) => {
  return (
    <Stack direction="row" justifyContent="space-between">
      <Typography variant="bodyMedium" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="labelLarge" color="text.secondary">
        {value}
      </Typography>
    </Stack>
  );
};

const ReviewSection = ({ title, items, sx }) => {
  return (
    <Stack sx={sx}>
      <Typography variant="labelLarge">{title}</Typography>
      <Stack gap={2}>
        {items.map((item) => (
          <ListItem key={item.key} label={item.label} value={item.value} />
        ))}
      </Stack>
    </Stack>
  );
};

const ReviewAndSubmitVoucherView = ({
  canSubmit,
  handlePrepareExecution,
  handleSubmitToTracs,
  hasSubmissionBlockers,
  hasUnsubmittedEligibleEvents,
  hasValidationBlocker,
  hudTracsIntegration,
  isBlockedByPrior,
  isReadOnly,
  isVoucherDraft,
  isVoucherEmpty,
  isVoucherLocked,
  nonDraftTracsTooltip,
  reviewDetails,
  saveExecutionLoading,
  savedExecutionData,
  submitToTracsDisabledTooltip,
  submitVoucherMutation,
  voucher,
}) => {
  const intl = useIntl();

  const unitsSectionItems = useMemo(
    () => [
      {
        key: 'unitsReceivingSubsidy',
        label: intl.formatMessage(messages.unitsReceivingSubsidyUnderContract),
        value: reviewDetails?.units?.unitsReceivingSubsidy,
      },
      {
        key: 'unitsAbated',
        label: intl.formatMessage(messages.unitsAbatedUnderThisContract),
        value: reviewDetails?.units?.unitsAbated,
      },
      {
        key: 'unitsVacant',
        label: intl.formatMessage(messages.unitsVacantUnderThisContract),
        value: reviewDetails?.units?.unitsVacant,
      },
      {
        key: 'marketRentTenants',
        label: intl.formatMessage(messages.occupiedByMarketRentTenants),
        value: reviewDetails?.units?.marketRentTenants,
      },
    ],
    [intl, reviewDetails?.units],
  );

  const accountingSectionItems = useMemo(() => {
    const {
      adjustmentsToRegularPayments,
      miscellaneousAccounting,
      regularTenantAssistancePayments,
      repaymentAgreements,
      specialClaims,
    } = reviewDetails?.accounting?.sections;

    return [
      {
        key: 'regularTenantAssistancePayments',
        label: intl.formatMessage(
          messages.regularTenantAssistancePaymentsLabel,
        ),
        requestedAmount: formatNullableCurrency(
          regularTenantAssistancePayments?.requestedAmount,
        ),
        caHudApprovedAmount: formatNullableCurrency(
          regularTenantAssistancePayments?.caHudApprovedAmount,
        ),
      },
      {
        key: 'adjustmentsToRegularPayments',
        label: intl.formatMessage(
          messages.adjustmentsToRegularTenantAssistancePaymentsLabel,
        ),
        requestedAmount: formatNullableCurrency(
          adjustmentsToRegularPayments?.requestedAmount,
        ),
        caHudApprovedAmount: formatNullableCurrency(
          adjustmentsToRegularPayments?.caHudApprovedAmount,
        ),
      },
      {
        key: 'specialClaims',
        label: intl.formatMessage(messages.specialClaimsLabel),
        requestedAmount: formatNullableCurrency(specialClaims?.requestedAmount),
        caHudApprovedAmount: formatNullableCurrency(
          specialClaims?.caHudApprovedAmount,
        ),
      },
      {
        key: 'repaymentAgreements',
        label: intl.formatMessage(messages.repaymentAgreementsLabel),
        requestedAmount: formatNullableCurrency(
          repaymentAgreements?.requestedAmount,
        ),
        caHudApprovedAmount: formatNullableCurrency(
          repaymentAgreements?.caHudApprovedAmount,
        ),
      },
      {
        key: 'miscellaneousAccounting',
        label: intl.formatMessage(messages.miscellaneousAccountingLabel),
        requestedAmount: formatNullableCurrency(
          miscellaneousAccounting?.requestedAmount,
        ),
        caHudApprovedAmount: formatNullableCurrency(
          miscellaneousAccounting?.caHudApprovedAmount,
        ),
      },
    ];
  }, [intl, reviewDetails?.accounting]);

  const totalsSectionItems = useMemo(() => {
    const { totals } = reviewDetails?.accounting;
    return [
      {
        key: 'totalVoucherAmount',
        label: intl.formatMessage(messages.totalVoucherAmountLabel),
        requestedAmount: formatNullableCurrency(totals?.requestedAmount),
        caHudApprovedAmount: formatNullableCurrency(
          totals?.caHudApprovedAmount,
        ),
      },
    ];
  }, [intl, reviewDetails?.accounting]);

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        gap={2}
      >
        <Typography variant="titleLarge" marginBottom={2}>
          {intl.formatMessage(messages.reviewVoucherDetailsTitle)}
        </Typography>
        <Stack direction="row" gap={2}>
          <PrepareExecutionModal
            onPrepareExecution={handlePrepareExecution}
            disabled={
              !canSubmit ||
              !isVoucherDraft ||
              hasSubmissionBlockers ||
              isVoucherEmpty ||
              isVoucherLocked
            }
            disabledTooltip={getFirstMatchingReason([
              {
                condition: !canSubmit,
                message: intl.formatMessage(parentMessages.noPermissionTooltip),
              },
              { condition: !isVoucherDraft, message: nonDraftTracsTooltip },
              {
                condition: isVoucherEmpty,
                message: intl.formatMessage(messages.voucherHasNoDataTooltip),
              },
              {
                condition: hasValidationBlocker,
                message: intl.formatMessage(messages.validationBlockingTooltip),
              },
              {
                condition: hasUnsubmittedEligibleEvents,
                message: intl.formatMessage(messages.reviewSubmitGatingBanner),
              },
            ])}
            voucher={voucher}
            initialValues={savedExecutionData}
            isSubmitting={saveExecutionLoading}
          />
          {hudTracsIntegration && (
            <SubmitToTracsModal
              onSend={handleSubmitToTracs}
              disabled={
                !canSubmit ||
                !isVoucherDraft ||
                !voucher?.id ||
                isVoucherEmpty ||
                savedExecutionData === null ||
                hasSubmissionBlockers ||
                isBlockedByPrior ||
                submitVoucherMutation.isLoading ||
                isVoucherLocked ||
                (isVoucherDraft && voucher?.hasDraftChangesSinceSubmission)
              }
              confirmLabel={intl.formatMessage(messages.confirmLabel)}
              title={intl.formatMessage(messages.modalTitle)}
              description={intl.formatMessage(messages.modalDescription)}
              buttonText={intl.formatMessage(messages.modalButtonText)}
              buttonTooltip={
                !canSubmit
                  ? intl.formatMessage(parentMessages.noPermissionTooltip)
                  : submitToTracsDisabledTooltip
              }
              submitButtonText={intl.formatMessage(messages.submitButtonText)}
            />
          )}
        </Stack>
      </Stack>

      {(hasUnsubmittedEligibleEvents || hasValidationBlocker) && !isReadOnly ? (
        <Banner
          status="warning"
          message={intl.formatMessage(
            hasValidationBlocker
              ? messages.validationBlockingBanner
              : messages.reviewSubmitGatingBanner,
          )}
        />
      ) : null}

      <Card>
        <Grid component={CardContent} container spacing={2}>
          <Grid
            item
            xs={12}
            md={4}
            sx={{ paddingRight: { xs: 0, md: 3, lg: 6, xl: 12 } }}
          >
            <ReviewSection
              title={intl.formatMessage(messages.unitsSectionTitle)}
              items={unitsSectionItems}
              sx={{ gap: 1, marginBottom: 1.5 }}
            />
          </Grid>
          <Grid
            item
            xs={12}
            md={8}
            sx={{ paddingRight: { xs: 0, md: 3, lg: 6, xl: 12 } }}
          >
            <AccountingSectionTable
              items={accountingSectionItems}
              sx={{ gap: 1, marginBottom: 3 }}
            />
            <AccountingSectionTable
              title={intl.formatMessage(messages.totalsSectionTitle)}
              totalsSection
              items={totalsSectionItems}
              sx={{ gap: 1, marginTop: 1.5 }}
            />
          </Grid>
        </Grid>
      </Card>
    </Box>
  );
};

export default ReviewAndSubmitVoucherView;
